import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { generateSlugFromTitle } from "@/utils/urlHelpers";
import { useAuth } from "./useAuth";
import { sessionManager } from "@/utils/sessionManager";
import { getUserIP } from "@/utils/ipUtils";

interface CreateTopicData {
  title: string;
  content: string;
  category_id: string;
}

export const useCreateTopic = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateTopicData) => {
      // Validate category
      const { data: category, error: categoryError } = await supabase
        .from("categories")
        .select("level, name, requires_moderation")
        .eq("id", data.category_id)
        .single();

      if (categoryError) throw new Error("Invalid category selected");

      if (category.level !== 2 && category.level !== 3) {
        throw new Error(
          `Topics can only be created in discussion or age group categories. "${category.name}" is for browsing only.`
        );
      }

      const baseSlug = generateSlugFromTitle(data.title);
      const uniqueSlug = `${baseSlug}-${Date.now().toString(36)}`;

      if (!user) {
        const tempUserId = sessionManager.getTempUserId();
        if (!tempUserId) throw new Error("No temporary user session available");
      }

      // Best-effort IP/UA capture for admin-only audit trail.
      const author_ip = await getUserIP().catch(() => null);
      const author_user_agent = typeof navigator !== "undefined" ? navigator.userAgent : null;

      const topicData: any = {
        title: data.title,
        content: data.content,
        category_id: data.category_id,
        slug: uniqueSlug,
        user_id: user?.id || null,
        author_ip,
        author_user_agent,
      };

      const { data: topic, error } = await supabase
        .from("topics")
        .insert(topicData)
        .select(`*, categories (name, slug, color)`)
        .single();

      if (error) throw error;
      return topic;
    },
    onSuccess: (topic) => {
      queryClient.invalidateQueries({ queryKey: ["topics", topic.category_id] });
      queryClient.invalidateQueries({ queryKey: ["topics"] });
    },
  });
};
